// Pure derivation of a caja movement into what the V2 movements list shows:
// tone/icon per type, signed amount, which actions the current user may take.
// The components only translate labels and lay the row out.
import { paymentMethodIcon } from '@/domain/payments/payment-method-icon';
import { CajaPaymentMethod } from '@/interfaces/ICaja';
import { ICajaMovement } from '@/interfaces/ICajaMovement';
import { IUser } from '@/interfaces/IUser';
import { roundPrice } from '@/utils';
import { paymentMethodToCajaMethodOrNull } from '@/utils/orders.utils';

export type TCajaMovementTone = 'in' | 'out' | 'neutral' | 'refunded';

/** In display order. */
export type TCajaMovementAction = 'viewOrders' | 'changeMethod' | 'refund';

export interface ICajaMovementRowContext {
  /** User holds CajaManage: refund and change-method endpoints require it. */
  canManage: boolean;
  /** Caja operated by a POS device: the server rejects manual money edits. */
  deviceBound: boolean;
  /** Refunds/re-tenders create movements, which a closed caja rejects. */
  cajaOpen: boolean;
}

/** Accepts API JSON shapes (string amounts/dates) like the payments box. */
export type TCajaMovementInput = Pick<
  ICajaMovement,
  | 'id'
  | 'type'
  | 'reimbursed'
  | 'description'
  | 'paymentMethod'
  | 'paymentId'
  | 'orderIds'
> & {
  amount: number | string;
  createdAt: Date | string;
  user?: Pick<IUser, 'name'> | null;
};

export interface ICajaMovementRow {
  id: number;
  tone: TCajaMovementTone;
  /** PosUiIcon name. */
  icon: string;
  /** i18n key of the movement type (cajaV2.type*). */
  typeKey: string;
  bucket: CajaPaymentMethod | null;
  /** The movement carries a payment method, mapped to a bucket or not. */
  hasMethod: boolean;
  userName: string | null;
  ordersCount: number;
  description: string | null;
  /** Always positive; `sign` carries the direction. */
  amount: number;
  sign: '+' | '-' | '';
  reimbursed: boolean;
  /** ISO string; '' when the source date is invalid. */
  createdAt: string;
  actions: TCajaMovementAction[];
}

const TYPE_KEYS: Record<ICajaMovement['type'], string> = {
  open: 'cajaV2.typeOpen',
  close: 'cajaV2.typeClose',
  inflow: 'cajaV2.typeInflow',
  outflow: 'cajaV2.typeOutflow',
};

const SIGNS: Record<ICajaMovement['type'], ICajaMovementRow['sign']> = {
  open: '',
  close: '',
  inflow: '+',
  outflow: '-',
};

function toneOf(m: TCajaMovementInput): TCajaMovementTone {
  if (m.reimbursed) return 'refunded';
  if (m.type === 'inflow') return 'in';
  if (m.type === 'outflow') return 'out';
  return 'neutral';
}

function iconOf(
  m: TCajaMovementInput,
  bucket: CajaPaymentMethod | null
): string {
  if (m.reimbursed) return 'cash-refund';
  if (m.type === 'open') return 'lock-open-variant-outline';
  if (m.type === 'close') return 'lock-outline';
  if (m.type === 'outflow' && bucket === 'cash') return 'cash-minus';
  return paymentMethodIcon(bucket);
}

/**
 * Mirrors the server gates (caja-movements.controller / v2.payments.controller):
 * money edits need CajaManage and an open, non-device-bound caja; only live
 * inflows that came from a payment or an order can be refunded/re-tendered.
 * Viewing orders needs no permission.
 */
function actionsOf(
  m: TCajaMovementInput,
  ordersCount: number,
  ctx: ICajaMovementRowContext
): TCajaMovementAction[] {
  const actions: TCajaMovementAction[] = [];
  if (ordersCount > 0) actions.push('viewOrders');

  const isLiveMoneyInflow =
    m.type === 'inflow' &&
    !m.reimbursed &&
    (m.paymentId != null || ordersCount > 0);
  const canEditMoney = ctx.canManage && ctx.cajaOpen && !ctx.deviceBound;
  if (canEditMoney && isLiveMoneyInflow) {
    actions.push('changeMethod', 'refund');
  }
  return actions;
}

export function getCajaMovementRow(
  m: TCajaMovementInput,
  ctx: ICajaMovementRowContext
): ICajaMovementRow {
  const bucket = paymentMethodToCajaMethodOrNull(m.paymentMethod);
  const ordersCount = m.orderIds?.length ?? 0;
  const createdMs = new Date(m.createdAt).getTime();
  const userName = m.user?.name?.trim() || null;
  const description = m.description?.trim() || null;

  return {
    id: m.id,
    tone: toneOf(m),
    icon: iconOf(m, bucket),
    typeKey: TYPE_KEYS[m.type],
    bucket,
    hasMethod: m.paymentMethod != null,
    userName,
    ordersCount,
    description,
    amount: roundPrice(Math.abs(+m.amount || 0)),
    sign: SIGNS[m.type],
    reimbursed: m.reimbursed,
    createdAt: Number.isFinite(createdMs)
      ? new Date(createdMs).toISOString()
      : '',
    actions: actionsOf(m, ordersCount, ctx),
  };
}

/**
 * Newest first; same instant → higher id first (creation order). Sessions
 * reach thousands of movements, so the date is parsed once per row, not
 * once per comparison.
 */
export function getCajaMovementRows(
  movements: readonly TCajaMovementInput[],
  ctx: ICajaMovementRowContext
): ICajaMovementRow[] {
  return movements
    .map((m) => {
      const row = getCajaMovementRow(m, ctx);
      return { row, ms: Date.parse(row.createdAt) };
    })
    .sort((a, b) => {
      // NaN (an invalid date on either side) is falsy: falls back to the id.
      const byTime = b.ms - a.ms;
      return byTime || b.row.id - a.row.id;
    })
    .map(({ row }) => row);
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

interface ITimeFormatters {
  time: Intl.DateTimeFormat;
  day: Intl.DateTimeFormat;
}

// `toLocale*String` builds a new Intl.DateTimeFormat per call (~0.4ms each in
// V8): with thousands of rows that is seconds per render. One pair per locale.
const FORMATTERS = new Map<string, ITimeFormatters>();

function formattersFor(locale: string): ITimeFormatters {
  let formatters = FORMATTERS.get(locale);
  if (!formatters) {
    formatters = {
      // h23, not `hour12: false`: older Chromium/WebViews resolve the latter
      // to h24 for h12-default locales (en, hi, ar...) and print "24:05".
      time: new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
      day: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' }),
    };
    FORMATTERS.set(locale, formatters);
  }
  return formatters;
}

/**
 * `HH:mm` when the movement happened on the same local day as `today`,
 * `d/M HH:mm` otherwise: caja sessions cross midnight and the date then
 * matters. Callers pass a `today` that only changes when the day does.
 */
export function formatCajaMovementTime(
  createdAt: string | Date,
  today: Date,
  locale: string
): string {
  const d = new Date(createdAt);
  if (!Number.isFinite(d.getTime())) return '';

  const { time, day } = formattersFor(locale);
  if (isSameLocalDay(d, today)) return time.format(d);
  return `${day.format(d)} ${time.format(d)}`;
}
